import { Input, Space, Typography } from 'antd'
import { CIMSModal } from 'components/CIMSModal'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import { HelpMessage } from 'utils/constants'
import { useGlobalState } from 'context/GlobalStateContext'
import { simpleEditNotification } from 'hooks/notificationHandler'
import CIMSFormItem from 'components/CIMSFormItem'
import { useProjectSettingsContext } from '../../../ProjectSettingsContext'
import CIMSSelect from 'components/CIMSSelect'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { useState } from 'react'
import CIMSLink from 'components/CIMSLink'
import { errorHandler } from 'hooks/errorHandler'

export const EditSharePointModal: React.FC<CIMSModalWrapperProps> = ({
  onClose,
  visible,
}) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    metadata,
    editMetadata,
    sharePointDrives,
    sharePointDrive,
    fetching,
    refetch,
  } = useProjectSettingsContext()
  const help = HelpMessage.Administration.ProjectSettings.SharePoint
  const [showWarning, setShowWarning] = useState(false)
  const close = () => {
    setShowWarning(false)
    onClose()
  }
  return (
    <CIMSModal
      title={'Edit SharePoint Landing Page'}
      onCancel={close}
      onOk={async ({ sharePointSiteLandingPage, customDrive }) => {
        if (
          customDrive?.value !== metadata?.customDriveId ||
          sharePointSiteLandingPage !== metadata?.sharePointSiteLandingPage
        ) {
          if (!projectId) {
            return errorHandler('Project id must be defined')
          }

          await editMetadata({
            variables: {
              editProjectMetadataInput: {
                projectId,
                sharePointSiteLandingPage: sharePointSiteLandingPage ?? null,
                customDriveId: (customDrive?.value as string) ?? null,
              },
            },
          })
          simpleEditNotification('SharePoint Settings')
          refetch()
        }
        close()
      }}
      visible={visible}
    >
      <CIMSFormItem
        label="SharePoint Landing Page"
        name="sharePointSiteLandingPage"
        help={help.LandingPage}
        initialValue={metadata?.sharePointSiteLandingPage}
      >
        <Input allowClear />
      </CIMSFormItem>
      <CIMSFormItem
        label="Custom Document Library"
        name="customDrive"
        help={help.CustomDrive}
        initialValue={
          metadata?.customDriveId
            ? ({
                label: sharePointDrive?.name ?? 'Unknown Drive',
                value: sharePointDrive?.id ?? metadata.customDriveId,
                tooltip: sharePointDrive?.description,
                deleted: !sharePointDrive,
              } as LabeledValueType)
            : undefined
        }
        description={
          !showWarning ? undefined : (
            <Space direction="vertical">
              <Typography.Text type="warning">
                Changing the Document Library will not migrate files from the
                previous library to the new one, it only changes where future
                files will be uploaded.
              </Typography.Text>
              <Typography.Text type="secondary">
                You can attempt to manually migrate to the new library by{' '}
                <CIMSLink
                  external
                  href="https://support.microsoft.com/en-us/office/move-or-copy-files-in-sharepoint-00e2f483-4df3-46be-a861-1f5f0c1a87bc"
                >
                  moving
                </CIMSLink>
                , not copying, each folder from the previous library to the new
                one.
              </Typography.Text>{' '}
            </Space>
          )
        }
      >
        <CIMSSelect
          onChange={() => setShowWarning(true)}
          loading={fetching}
          options={sharePointDrives}
        />
      </CIMSFormItem>
    </CIMSModal>
  )
}
